import { useState } from 'react';
import { Sparkles, TrendingUp, Hash, Layout, Loader2, AlertCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { enhanceContent } from '../../../services/aiContentEnhancer';

const ContentEnhancementPanel = ({ content, platforms, onApplySuggestion }) => {
  const [enhancements, setEnhancements] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('openai');

  const handleEnhance = async () => {
    if (!content?.trim()) {
      setError('Please enter some content first');
      return;
    }

    if (!platforms?.length) {
      setError('Please select at least one platform');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const result = await enhanceContent(content, platforms, selectedProvider);
      setEnhancements(result);
    } catch (err) {
      setError(err?.message || 'Failed to enhance content');
      console.error('Enhancement error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyHashtags = (hashtags) => {
    const hashtagText = hashtags?.join(' ');
    onApplySuggestion?.(content + '\n\n' + hashtagText);
  };

  const handleApplyFormatting = (platformId, formattedContent) => {
    onApplySuggestion?.(formattedContent);
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">AI Content Enhancement</h3>
        </div>
        <select
          value={selectedProvider}
          onChange={(e) => setSelectedProvider(e?.target?.value)}
          className="px-3 py-1.5 rounded-lg border border-purple-300 bg-white text-sm font-medium text-gray-700 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="openai">OpenAI GPT-4</option>
          <option value="claude">Claude 3.5</option>
          <option value="gemini">Gemini 2.0</option>
        </select>
      </div>

      {/* Enhance Button */}
      <Button
        onClick={handleEnhance}
        disabled={loading || !content?.trim() || !platforms?.length}
        variant="default"
        fullWidth
        className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Analyzing Content...
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4 mr-2" />
            Enhance Content
          </>
        )}
      </Button>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Enhancement Results */}
      {enhancements && (
        <div className="space-y-4">
          {/* Tone Analysis */}
          {enhancements?.tone && (
            <div className="bg-white rounded-lg p-4 border border-purple-200">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-purple-600" />
                <h4 className="font-semibold text-gray-900">Tone Analysis</h4>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Current Tone:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {enhancements?.tone?.currentTone}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Tone Score:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {enhancements?.tone?.toneScore}/10
                  </span>
                </div>
                {enhancements?.tone?.suggestions && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-1">Suggestions:</p>
                    <p className="text-sm text-gray-700">{enhancements?.tone?.suggestions}</p>
                  </div>
                )}
                {enhancements?.tone?.alternatives && enhancements?.tone?.alternatives?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-1">Alternative Tones:</p>
                    <div className="flex flex-wrap gap-2">
                      {enhancements?.tone?.alternatives?.map((alt, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full"
                        >
                          {alt}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Hashtag Suggestions */}
          {enhancements?.hashtags && (
            <div className="bg-white rounded-lg p-4 border border-purple-200">
              <div className="flex items-center gap-2 mb-3">
                <Hash className="w-4 h-4 text-purple-600" />
                <h4 className="font-semibold text-gray-900">Hashtag Suggestions</h4>
              </div>
              <div className="space-y-3">
                {enhancements?.hashtags?.trending && enhancements?.hashtags?.trending?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-gray-600">Trending</p>
                      <Button
                        onClick={() => handleApplyHashtags(enhancements?.hashtags?.trending)}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                      >
                        Apply All
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {enhancements?.hashtags?.trending?.map((tag, idx) => (
                        <button
                          key={idx}
                          onClick={() => onApplySuggestion?.(content + ' ' + tag)}
                          className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full hover:bg-blue-200 transition-colors"
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {enhancements?.hashtags?.niche && enhancements?.hashtags?.niche?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-gray-600">Niche-Specific</p>
                      <Button
                        onClick={() => handleApplyHashtags(enhancements?.hashtags?.niche)}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                      >
                        Apply All
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {enhancements?.hashtags?.niche?.map((tag, idx) => (
                        <button
                          key={idx}
                          onClick={() => onApplySuggestion?.(content + ' ' + tag)}
                          className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full hover:bg-green-200 transition-colors"
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {enhancements?.hashtags?.branded && enhancements?.hashtags?.branded?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-gray-600">Branded</p>
                      <Button
                        onClick={() => handleApplyHashtags(enhancements?.hashtags?.branded)}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                      >
                        Apply All
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {enhancements?.hashtags?.branded?.map((tag, idx) => (
                        <button
                          key={idx}
                          onClick={() => onApplySuggestion?.(content + ' ' + tag)}
                          className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full hover:bg-purple-200 transition-colors"
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {enhancements?.hashtags?.optimal && (
                  <p className="text-xs text-gray-500 mt-2">
                    Recommended: Use {enhancements?.hashtags?.optimal} hashtags for optimal reach
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Platform Formatting */}
          {enhancements?.formatting && (
            <div className="bg-white rounded-lg p-4 border border-purple-200">
              <div className="flex items-center gap-2 mb-3">
                <Layout className="w-4 h-4 text-purple-600" />
                <h4 className="font-semibold text-gray-900">Platform-Specific Formatting</h4>
              </div>
              <div className="space-y-3">
                {Object.entries(enhancements?.formatting || {})?.map(([platform, data]) => (
                  <div key={platform} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-sm font-medium text-gray-900 capitalize">{platform}</h5>
                      <Button
                        onClick={() => handleApplyFormatting(platform, data?.formattedContent)}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                      >
                        Apply
                      </Button>
                    </div>
                    {data?.formattedContent && (
                      <div className="bg-gray-50 rounded p-2 mb-2">
                        <p className="text-xs text-gray-700 line-clamp-3">{data?.formattedContent}</p>
                      </div>
                    )}
                    {data?.characterCount && (
                      <p className="text-xs text-gray-500 mb-1">
                        Character count: {data?.characterCount}
                      </p>
                    )}
                    {data?.suggestions && data?.suggestions?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-gray-600 mb-1">Suggestions:</p>
                        <ul className="text-xs text-gray-600 space-y-1">
                          {data?.suggestions?.map((suggestion, idx) => (
                            <li key={idx} className="flex items-start gap-1">
                              <span className="text-purple-600 mt-0.5">•</span>
                              <span>{suggestion}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentEnhancementPanel;